import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { apiService } from '../services/apiService';
import { useToast } from '../contexts/ToastContext';

interface UniversalOSINTRequest {
  id: number;
  user: {
    id: number;
    email: string;
    firstName: string;
    lastName: string;
  };
  search_value: string;
  parameter_type: {
    id: number;
    name: string;
    display_name: string;
  };
  status?: 'pending' | 'processing' | 'completed' | 'failed';
  tools_executed: number;
  successful_tools: string[];
  failed_tools: string[];
  success_rate: number;
  processing_time: number;
  risk_assessment?: string;
  confidence_score: number;
  created_at: string;
  completed_at?: string;
  error_message?: string;
}

interface UniversalOSINTDetails extends UniversalOSINTRequest {
  results: any;
  summary: any;
}

const AdminUniversalOSINTPage: React.FC = () => {
  const [requests, setRequests] = useState<UniversalOSINTRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<UniversalOSINTDetails | null>(null);
  const [showReport, setShowReport] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({
    status: '',
    parameter_type: '',
    search: ''
  });
  const [refreshingId, setRefreshingId] = useState<number | null>(null);
  
  const { showToast } = useToast();

  const loadRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage.toString(),
        page_size: '20',
        ...filters
      };
      
      console.log('Loading Universal OSINT requests with params:', params);
      const response = await apiService.getAdminUniversalOSINTRequests(params);
      console.log('API Response:', response);
      
      setRequests(response.results || []);
      setTotalCount(response.count || 0);
      
      console.log('Set requests:', response.results?.length || 0, 'items');
      console.log('Set total count:', response.count || 0);
    } catch (error) {
      console.error('Failed to load Universal OSINT requests:', error);
      showToast('Failed to load requests', 'error');
    } finally {
      setLoading(false);
    }
  }, [currentPage, filters, showToast]);

  const submitRequest = async () => {
    if (!searchValue.trim()) {
      showToast('Please enter a search value', 'error');
      return;
    }

    setSubmitting(true);
    const optimisticId = Date.now();
    const optimisticRequest: UniversalOSINTRequest = {
      id: optimisticId,
      user: {
        id: 0,
        email: 'You',
        firstName: '',
        lastName: ''
      },
      search_value: searchValue.trim(),
      parameter_type: { id: 0, name: '', display_name: '' },
      status: 'processing',
      tools_executed: 0,
      successful_tools: [],
      failed_tools: [],
      success_rate: 0,
      processing_time: 0,
      risk_assessment: '',
      confidence_score: 0,
      created_at: new Date().toISOString(),
      completed_at: undefined,
      error_message: undefined
    };
    setRequests(prev => [optimisticRequest, ...(Array.isArray(prev) ? prev : [])]);
    try {
      await apiService.submitAdminUniversalOSINTRequest({
        search_value: searchValue.trim(),
        request_data: {}
      });

      showToast('Universal OSINT request submitted successfully', 'success');
      setSearchValue('');
      // Replace optimistic row with real data
      loadRequests();
    } catch (error) {
      setRequests(prev => (Array.isArray(prev) ? prev.filter(r => r.id !== optimisticId) : []));
      console.error('Failed to submit Universal OSINT request:', error);
      showToast('Failed to submit request', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const loadRequestDetails = async (requestId: number) => {
    try {
      const response = await apiService.getAdminUniversalOSINTRequestDetails(requestId);
      setSelectedRequest(response);
      setShowReport(true);
    } catch (error) {
      console.error('Failed to load request details:', error);
      showToast('Failed to load request details', 'error');
    }
  };

  const refreshRequestStatus = async (requestId: number) => {
    setRefreshingId(requestId);
    try {
      const updated = await apiService.getAdminUniversalOSINTRequestDetails(requestId);
      setRequests(prev => {
        if (!Array.isArray(prev)) return [];
        return prev.map(r => r.id === requestId ? { ...r, ...updated } : r);
      });
      showToast('Request status updated', 'success');
    } catch (error) {
      showToast('Failed to refresh status', 'error');
    } finally {
      setRefreshingId(null);
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const getStatusBadge = (status: string | undefined | null) => {
    // Handle undefined or null status
    if (!status) {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700">
          <span className="text-xs">❓</span>
          Unknown
        </span>
      );
    }

    const statusConfig = {
      pending: { color: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 border-yellow-200 dark:border-yellow-800', icon: '⏳' },
      processing: { color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 border-blue-200 dark:border-blue-800', icon: '🔄' },
      completed: { color: 'bg-green-100 dark:bg-green-900/30 text-green-800 border-green-200 dark:border-green-800', icon: '✅' },
      failed: { color: 'bg-red-100 dark:bg-red-900/30 text-red-800 border-red-200 dark:border-red-800', icon: '❌' }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${config.color}`}>
        <span className="text-xs">{config.icon}</span>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getSuccessRateColor = (rate: number) => {
    if (rate >= 90) return 'text-green-600 dark:text-green-400';
    if (rate >= 70) return 'text-yellow-600 dark:text-yellow-400';
    if (rate >= 50) return 'text-orange-600';
    return 'text-red-600 dark:text-red-400';
  };

  const getDurationColor = (duration: number) => {
    if (duration <= 5) return 'text-green-600 dark:text-green-400';
    if (duration <= 15) return 'text-yellow-600 dark:text-yellow-400';
    if (duration <= 30) return 'text-orange-600';
    return 'text-red-600 dark:text-red-400';
  };

  const truncateText = (text: string, maxLength: number = 25) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds.toFixed(0)}s`;
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast('Copied to clipboard!', 'success');
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      showToast('Copied to clipboard!', 'success');
    }
  };

  // Mobile card view for requests
  const RequestCard: React.FC<{ request: UniversalOSINTRequest }> = ({ request }) => (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 mb-4 shadow-sm hover:shadow-md transition-shadow duration-200">
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-sm font-bold text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">#{request.id}</span>
            {getStatusBadge(request.status)}
          </div>
          <div className="space-y-2">
            <div className="text-sm">
              <span className="font-medium text-gray-700 dark:text-gray-200">Search:</span>
              <span className="ml-1 text-gray-900 dark:text-white break-all">{truncateText(request.search_value, 30)}</span>
            </div>
            <div className="text-sm">
              <span className="font-medium text-gray-700 dark:text-gray-200">Type:</span>
              <span className="ml-1 text-gray-900 dark:text-white">{request.parameter_type.display_name}</span>
            </div>
            <div className="text-sm">
              <span className="font-medium text-gray-700 dark:text-gray-200">User:</span>
              <span className="ml-1 text-gray-900 dark:text-white">{request.user.email}</span>
            </div>
          </div>
        </div>
        <div className="text-right text-xs text-gray-500 dark:text-gray-400 ml-2">
          {formatDate(request.created_at)}
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
        <div className="bg-gray-50 dark:bg-gray-900 p-2 rounded">
          <div className="text-gray-500 dark:text-gray-400 text-xs">Success Rate</div>
          <div className={`font-bold ${getSuccessRateColor(request.success_rate || 0)}`}>
            {(request.success_rate || 0).toFixed(1)}%
          </div>
        </div>
        <div className="bg-gray-50 dark:bg-gray-900 p-2 rounded">
          <div className="text-gray-500 dark:text-gray-400 text-xs">Duration</div>
          <div className={`font-bold ${getDurationColor(request.processing_time || 0)}`}>
            {formatDuration(request.processing_time || 0)}
          </div>
        </div>
      </div>
      
      <div className="flex gap-2">
        <Button
          onClick={() => loadRequestDetails(request.id)}
          variant="outline"
          size="sm"
          className="flex-1 bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 text-indigo-700 hover:bg-indigo-100 dark:bg-indigo-900/30"
        >
          📊 View Report
        </Button>
        <Button
          onClick={() => refreshRequestStatus(request.id)}
          variant="outline"
          size="sm"
          disabled={refreshingId === request.id}
          className="flex-1"
        >
          {refreshingId === request.id ? '🔄 Refreshing...' : '🔄 Refresh'}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-6 lg:py-8 max-w-7xl">
      {/* Header with Stats */}
      <div className="mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-4">
          <div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-2">Universal OSINT Management</h1>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300">Submit and monitor Universal OSINT requests</p>
          </div>
          <div className="flex gap-2">
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg px-3 py-2 text-center">
              <div className="text-green-800 font-bold text-lg">{totalCount}</div>
              <div className="text-green-600 dark:text-green-400 text-xs">Total Requests</div>
            </div>
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-3 py-2 text-center">
              <div className="text-blue-800 font-bold text-lg">
                {Array.isArray(requests) ? requests.filter(r => r.status === 'completed').length : 0}
              </div>
              <div className="text-blue-600 dark:text-blue-400 text-xs">Completed</div>
            </div>
          </div>
        </div>
      </div>

      {/* Submit New Request */}
      <Card className="mb-6 sm:mb-8 border-2 border-indigo-100 bg-gradient-to-r from-indigo-50 to-purple-50">
        <div className="p-4 sm:p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center">
              <span className="text-white text-sm font-bold">+</span>
            </div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">Submit New Request</h2>
          </div>
          <div className="space-y-4 sm:space-y-0 sm:flex sm:gap-4">
            <div className="flex-1 min-w-0">
              <label htmlFor="searchValue" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Search Value
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="searchValue"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder="Enter email, phone, IP, or domain..."
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base"
                  disabled={submitting}
                />
                {submitting && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-end">
              <Button
                onClick={submitRequest}
                disabled={submitting || !searchValue.trim()}
                className="w-full sm:w-auto px-6 py-3 h-12 sm:h-11 text-sm sm:text-base bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500"
              >
                {submitting ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Submitting...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span>🔍</span>
                    Submit Request
                  </div>
                )}
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Filters */}
      <Card className="mb-6 border border-gray-200 dark:border-gray-700">
        <div className="p-4 sm:p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-6 h-6 bg-gray-600 rounded-full flex items-center justify-center">
              <span className="text-white text-xs font-bold">⚙️</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Filters</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Status</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base"
              >
                <option value="">All Status</option>
                <option value="pending">⏳ Pending</option>
                <option value="processing">🔄 Processing</option>
                <option value="completed">✅ Completed</option>
                <option value="failed">❌ Failed</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Parameter Type</label>
              <select
                value={filters.parameter_type}
                onChange={(e) => handleFilterChange('parameter_type', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base"
              >
                <option value="">All Types</option>
                <option value="email">📧 Email</option>
                <option value="phone_number">📱 Phone Number</option>
                <option value="ip_address">🌐 IP Address</option>
                <option value="domain">🌍 Domain</option>
              </select>
            </div>
            <div className="sm:col-span-2 lg:col-span-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Search</label>
              <input
                type="text"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="Search by value..."
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base"
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Requests List */}
      <Card className="border border-gray-200 dark:border-gray-700">
        <div className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 gap-3">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                <span className="text-white text-xs font-bold">📋</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Universal OSINT Requests</h3>
            </div>
            <div className="flex items-center gap-2">
              <div className="bg-gray-100 dark:bg-gray-800 px-3 py-1 rounded-full text-sm text-gray-600 dark:text-gray-300">
                Total: {totalCount} requests
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-gray-300 font-medium">Loading requests...</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Please wait while we fetch your data</p>
            </div>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="block sm:hidden">
                {(Array.isArray(requests) ? requests : []).map((request) => (
                  <RequestCard key={request.id} request={request} />
                ))}
                {(!Array.isArray(requests) || requests.length === 0) && (
                  <div className="text-center py-12">
                    <div className="text-gray-400 dark:text-gray-500 text-4xl mb-4">📋</div>
                    <p className="text-gray-600 dark:text-gray-300 font-medium">No Universal OSINT requests found</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Submit your first request above to get started</p>
                  </div>
                )}
              </div>

              {/* Desktop Table View */}
              <div className="hidden sm:block rounded-lg border border-gray-200 dark:border-gray-700 overflow-x-auto custom-scrollbar">
                <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-24">
                        Actions
                      </th>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-16">
                        ID
                      </th>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-48">
                        User
                      </th>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider min-w-0">
                        Search Value
                      </th>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-24">
                        Type
                      </th>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-20">
                        Status
                      </th>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-20">
                        Success
                      </th>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-16">
                        Time
                      </th>
                      <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-32">
                        Created
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {(Array.isArray(requests) ? requests : []).map((request) => (
                      <tr key={request.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150">
                        <td className="px-2 py-3 whitespace-nowrap text-xs font-medium">
                          <div className="flex flex-col gap-1">
                            <Button
                              onClick={() => loadRequestDetails(request.id)}
                              variant="outline"
                              size="sm"
                              className="text-xs px-2 py-1 bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 text-indigo-700 hover:bg-indigo-100 dark:bg-indigo-900/30"
                            >
                              📊 View
                            </Button>
                            <Button
                              onClick={() => refreshRequestStatus(request.id)}
                              variant="outline"
                              size="sm"
                              disabled={refreshingId === request.id}
                              className="text-xs px-2 py-1"
                            >
                              {refreshingId === request.id ? '🔄' : '🔄'}
                            </Button>
                          </div>
                        </td>
                        <td className="px-2 py-3 whitespace-nowrap">
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100">
                            #{request.id}
                          </span>
                        </td>
                        <td className="px-2 py-3 whitespace-nowrap text-xs text-gray-900 dark:text-white">
                          <div className="max-w-40">
                            <div className="font-medium text-gray-900 dark:text-white truncate" title={request.user.email}>
                              {request.user.email}
                            </div>
                            <div className="text-gray-500 dark:text-gray-400 truncate" title={`${request.user.firstName} ${request.user.lastName}`}>
                              {request.user.firstName} {request.user.lastName}
                            </div>
                          </div>
                        </td>
                        <td className="px-2 py-3 text-xs text-gray-900 dark:text-white">
                          <div className="max-w-48 truncate" title={request.search_value}>
                            {request.search_value}
                          </div>
                        </td>
                        <td className="px-2 py-3 whitespace-nowrap text-xs text-gray-900 dark:text-white">
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800">
                            {request.parameter_type.display_name}
                          </span>
                        </td>
                        <td className="px-2 py-3 whitespace-nowrap">
                          {getStatusBadge(request.status)}
                        </td>
                        <td className="px-2 py-3 whitespace-nowrap text-xs">
                          <div className={`font-bold ${getSuccessRateColor(request.success_rate || 0)}`}>
                            {(request.success_rate || 0).toFixed(1)}%
                          </div>
                        </td>
                        <td className="px-2 py-3 whitespace-nowrap text-xs">
                          <div className={`font-bold ${getDurationColor(request.processing_time || 0)}`}>
                            {formatDuration(request.processing_time || 0)}
                          </div>
                        </td>
                        <td className="px-2 py-3 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                          <div className="max-w-28 truncate" title={formatDate(request.created_at)}>
                            {formatDate(request.created_at)}
                          </div>
                        </td>
                      </tr>
                    )) || (
                      <tr>
                        <td colSpan={9} className="px-2 py-12 text-center">
                          <div className="text-gray-400 dark:text-gray-500 text-4xl mb-4">📋</div>
                          <p className="text-gray-600 dark:text-gray-300 font-medium">No Universal OSINT requests found</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Submit your first request above to get started</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Pagination */}
          {totalCount > 20 && (
            <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="text-sm text-gray-700 dark:text-gray-200">
                Page {currentPage} of {Math.ceil(totalCount / 20)}
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  variant="outline"
                  size="sm"
                  className="h-10 px-4"
                >
                  ← Previous
                </Button>
                <Button
                  onClick={() => setCurrentPage(prev => prev + 1)}
                  disabled={currentPage >= Math.ceil(totalCount / 20)}
                  variant="outline"
                  size="sm"
                  className="h-10 px-4"
                >
                  Next →
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Full Page Report Drawer */}
      {showReport && selectedRequest && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4 animate-in fade-in duration-200">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700/80 w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex justify-between items-center p-3 sm:p-4 lg:p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-base sm:text-lg lg:text-2xl font-bold text-gray-900 dark:text-white">
                Universal OSINT Report - #{selectedRequest.id}
              </h2>
              <Button
                onClick={() => setShowReport(false)}
                variant="outline"
                size="sm"
                className="h-8 sm:h-10 px-3 sm:px-4 text-xs sm:text-sm"
              >
                Close
              </Button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto custom-scrollbar p-3 sm:p-4 lg:p-6 space-y-4 sm:space-y-6">
              {/* Summary */}
              <Card>
                <div className="p-3 sm:p-4">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4">Summary</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                    <div>
                      <div className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400">Search Value</div>
                      <div className="text-xs sm:text-sm text-gray-900 dark:text-white break-all">{selectedRequest.search_value}</div>
                    </div>
                    <div>
                      <div className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400">Parameter Type</div>
                      <div className="text-xs sm:text-sm text-gray-900 dark:text-white">{selectedRequest.parameter_type.display_name}</div>
                    </div>
                    <div>
                      <div className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400">Status</div>
                      <div>{getStatusBadge(selectedRequest.status)}</div>
                    </div>
                    <div>
                      <div className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400">Success Rate</div>
                      <div className="text-xs sm:text-sm text-gray-900 dark:text-white">{(selectedRequest.success_rate || 0).toFixed(1)}%</div>
                    </div>
                    <div>
                      <div className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400">Tools Executed</div>
                      <div className="text-xs sm:text-sm text-gray-900 dark:text-white">{selectedRequest.tools_executed}</div>
                    </div>
                    <div>
                      <div className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400">Processing Time</div>
                      <div className="text-xs sm:text-sm text-gray-900 dark:text-white">{formatDuration(selectedRequest.processing_time || 0)}</div>
                    </div>
                    <div>
                      <div className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400">Risk Assessment</div>
                      <div className="text-xs sm:text-sm text-gray-900 dark:text-white">{selectedRequest.risk_assessment}</div>
                    </div>
                    <div>
                      <div className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400">Confidence Score</div>
                      <div className="text-xs sm:text-sm text-gray-900 dark:text-white">{(selectedRequest.confidence_score || 0).toFixed(1)}%</div>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Tools Results */}
              {selectedRequest.results && (
                <Card>
                  <div className="p-3 sm:p-4">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4">Tools Results</h3>
                    <div className="space-y-3 sm:space-y-4">
                      {Object.entries(selectedRequest.results.individual_results || {}).map(([toolName, result]: [string, any]) => (
                        <div key={toolName} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 sm:p-4">
                          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-2 gap-2">
                            <h4 className="font-medium text-gray-900 dark:text-white text-sm sm:text-base">{toolName}</h4>
                            <div className="flex items-center gap-2">
                              {result.error ? (
                                <Badge variant="error">Failed</Badge>
                              ) : (
                                <Badge variant="success">Success</Badge>
                              )}
                              <Button
                                onClick={() => copyToClipboard(JSON.stringify(result, null, 2))}
                                variant="outline"
                                size="sm"
                                className="h-6 sm:h-8 px-2 sm:px-3 text-xs"
                              >
                                📋
                              </Button>
                            </div>
                          </div>
                          {result.error && (
                            <div className="text-xs sm:text-sm text-red-600 dark:text-red-400">{result.error}</div>
                          )}
                          {!result.error && result.github_tools && (
                            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-300">
                              <div className="mb-2">
                                <strong>Tools Used:</strong> {result.github_tools.tools_used?.join(', ') || 'None'}
                              </div>
                              {result.github_tools.results && (
                                <div className="space-y-2">
                                  {Object.entries(result.github_tools.results || {}).map(([tool, toolResult]: [string, any]) => (
                                    <div key={tool} className="ml-2 sm:ml-4">
                                      <div className="font-medium text-gray-700 dark:text-gray-200 text-xs sm:text-sm">{tool}:</div>
                                      <div className="text-xs text-gray-600 dark:text-gray-300 overflow-x-auto">
                                        <pre className="whitespace-pre-wrap break-all">
                                          {JSON.stringify(toolResult, null, 2)}
                                        </pre>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>
              )}

              {/* Summary Data */}
              {selectedRequest.summary && (
                <Card>
                  <div className="p-3 sm:p-4">
                    <div className="flex justify-between items-center mb-3 sm:mb-4">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">Analysis Summary</h3>
                      <Button
                        onClick={() => copyToClipboard(JSON.stringify(selectedRequest.summary, null, 2))}
                        variant="outline"
                        size="sm"
                        className="h-8 sm:h-10 px-3 sm:px-4 text-xs sm:text-sm"
                      >
                        <span className="text-gray-600 dark:text-gray-300 mr-1">📋</span> Copy
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {selectedRequest.summary.key_findings?.map((finding: string, index: number) => (
                        <div key={index} className="text-xs sm:text-sm text-gray-700 dark:text-gray-200">• {finding}</div>
                      )) || (
                        <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">No key findings available</div>
                      )}
                    </div>
                  </div>
                </Card>
              )}

              {/* Complete API Response */}
              <Card>
                <div className="p-3 sm:p-4">
                  <div className="flex justify-between items-center mb-3 sm:mb-4">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">Complete API Response</h3>
                    <Button
                      onClick={() => copyToClipboard(JSON.stringify(selectedRequest, null, 2))}
                      variant="outline"
                      size="sm"
                      className="h-8 sm:h-10 px-3 sm:px-4 text-xs sm:text-sm"
                    >
                      <span className="text-gray-600 dark:text-gray-300 mr-1">📋</span> Copy
                    </Button>
                  </div>
                  <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-3 sm:p-4 overflow-x-auto">
                    <pre className="text-xs text-gray-800 dark:text-gray-100 whitespace-pre-wrap break-all">
                      {JSON.stringify(selectedRequest, null, 2)}
                    </pre>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUniversalOSINTPage; 